import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FaStar,
  FaCode,
  FaMobileAlt,
  FaRocket,
  FaLock,
  FaCheckCircle,
  FaCreditCard,
  FaUserCheck,
  FaWordpress,
  FaReact,
  FaNodeJs,
  FaHandshake,
  FaChartLine,
  FaSun,
  FaMoon,
  FaCalendarAlt,
  FaGem
} from 'react-icons/fa';
import { BsSpeedometer, BsLightningCharge, BsShield } from 'react-icons/bs';
import { SiTailwindcss, SiMongodb } from 'react-icons/si';
import { GiCrystalBall, GiStarsStack, GiPalm } from 'react-icons/gi';

const AstrologerSiteLanding = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    websiteType: 'basic'
  });

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    submitting: false,
    error: false,
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus({ submitting: true, submitted: false, error: false, message: '' });
    
    // Simulate form submission
    setTimeout(() => {
      setFormStatus({
        submitting: false,
        submitted: true,
        error: false,
        message: 'Thank you! We will contact you shortly to discuss your astrology website needs.'
      });
    }, 1500);
  };

  const benefits = [
    {
      icon: <BsSpeedometer className="text-4xl mb-4 text-cyber-pink" />,
      title: "Lightning Fast Performance",
      description: "Our astrology websites are optimized for speed, ensuring visitors stay engaged and stars align perfectly."
    },
    {
      icon: <FaMobileAlt className="text-4xl mb-4 text-cyber-pink" />,
      title: "Responsive Design",
      description: "Your site will look perfect on all devices - from desktop computers to mobile phones."
    },
    {
      icon: <FaLock className="text-4xl mb-4 text-cyber-pink" />,
      title: "Enhanced Security",
      description: "We implement top-tier security practices to keep your client data and business safe."
    },
    {
      icon: <FaRocket className="text-4xl mb-4 text-cyber-pink" />,
      title: "SEO Optimized",
      description: "Get found by potential clients looking for astrology services in your area."
    },
    {
      icon: <FaHandshake className="text-4xl mb-4 text-cyber-pink" />,
      title: "Client Booking System",
      description: "Integrated appointment scheduling and payment processing for your readings and consultations."
    },
    {
      icon: <FaChartLine className="text-4xl mb-4 text-cyber-pink" />,
      title: "Growth Analytics",
      description: "Track visitor behavior and conversion metrics to grow your astrology business."
    }
  ];

  const packages = [
    {
      name: "Basic",
      price: "₹25,000",
      description: "Perfect for astrologers just starting their online journey",
      features: [
        "Professional responsive website",
        "5 custom pages",
        "Basic appointment booking",
        "Contact form",
        "Social media integration",
        "3 months free hosting"
      ],
      recommended: false,
      value: "basic"
    },
    {
      name: "Professional",
      price: "₹45,000",
      description: "Our most popular choice for established astrologers",
      features: [
        "Everything in Basic package",
        "10 custom pages",
        "Advanced appointment system",
        "Online payment integration",
        "Blog/Article section",
        "Custom zodiac profiles",
        "6 months free hosting",
        "SEO optimization"
      ],
      recommended: true,
      value: "professional"
    },
    {
      name: "Premium",
      price: "₹75,000",
      description: "Complete digital solution for leading astrology practices",
      features: [
        "Everything in Professional package",
        "Unlimited pages",
        "Custom client portal",
        "Chart generation tools",
        "Online store for products",
        "Email marketing integration",
        "Mobile app development",
        "1 year free hosting",
        "Monthly maintenance & updates"
      ],
      recommended: false,
      value: "premium"
    }
  ];

  const technologies = [
    { name: "React", icon: <FaReact className="text-4xl text-blue-400" /> },
    { name: "Node.js", icon: <FaNodeJs className="text-4xl text-green-500" /> },
    { name: "WordPress", icon: <FaWordpress className="text-4xl text-blue-600" /> },
    { name: "Tailwind CSS", icon: <SiTailwindcss className="text-4xl text-teal-400" /> },
    { name: "MongoDB", icon: <SiMongodb className="text-4xl text-green-600" /> }
  ];

  const testimonials = [
    {
      name: "Maya Sharma",
      business: "Cosmic Insights Astrology",
      image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=150&q=80",
      quote: "My astrology business increased by 70% within three months of launching my new website. The booking system has automated my entire scheduling process!"
    },
    {
      name: "Rajesh Kapoor",
      business: "StarGaze Predictions",
      image: "https://images.unsplash.com/photo-1566492031773-4f4e44671857?auto=format&fit=crop&w=150&q=80",
      quote: "The team understood my vision perfectly. My website captures the mystical essence of my practice while being extremely functional and easy to use."
    },
    {
      name: "Priya Nair",
      business: "Zodiac Harmony",
      image: "https://images.unsplash.com/photo-1487573884658-a5d3c667584e?auto=format&fit=crop&w=150&q=80",
      quote: "I was hesitant to invest in a professional website, but it has paid for itself many times over. The beautiful design and client portal have completely transformed my business."
    }
  ];

  const workProcess = [
    {
      step: 1,
      title: "Discovery",
      description: "We learn about your astrology practice, style, and business goals."
    },
    {
      step: 2,
      title: "Design",
      description: "Our team creates custom mockups matching your cosmic branding."
    },
    {
      step: 3,
      title: "Development",
      description: "We build your site with clean code and optimized performance."
    },
    {
      step: 4,
      title: "Testing",
      description: "Rigorous testing ensures everything works flawlessly."
    },
    {
      step: 5,
      title: "Launch",
      description: "Your new astrology website goes live to the world."
    },
    {
      step: 6,
      title: "Support",
      description: "Ongoing maintenance keeps your site running smoothly."
    }
  ];

  // Project examples
  const projectExamples = [
    {
      name: "Cosmic Insights Astrology",
      image: "https://images.unsplash.com/photo-1596636478939-6c22e2ba5bc0?auto=format&fit=crop&w=800&h=500&q=80",
      description: "A luxurious website for a premium astrologer featuring birth chart analysis, personalized readings, and e-commerce.",
      features: [
        "Interactive birth chart generator",
        "Appointment booking system",
        "Secure payment processing",
        "Client testimonials showcase"
      ],
      url: "#"
    },
    {
      name: "Zodiac Harmony",
      image: "https://images.unsplash.com/photo-1532968801329-4db5a1a0e54c?auto=format&fit=crop&w=800&h=500&q=80",
      description: "A modern astrology platform for a team of astrologers offering combined Western and Vedic astrology services.",
      features: [
        "Multi-practitioner booking system",
        "Compatibility calculator",
        "Daily horoscope subscription",
        "Video consultation platform"
      ],
      url: "#"
    },
    {
      name: "AstralGuidance",
      image: "https://images.unsplash.com/photo-1515377905703-c4788e51af15?auto=format&fit=crop&w=800&h=500&q=80",
      description: "A mobile-first astrology business focusing on educational content and personalized transit forecasts.",
      features: [
        "Members-only content area",
        "Integrated blog and podcast",
        "Mobile app integration",
        "Automated transit reports"
      ],
      url: "#"
    }
  ];
  
  // Astrology services
  const astrologyServices = [
    {
      icon: <GiCrystalBall className="text-5xl mb-6 text-cyber-pink" />,
      title: "Birth Chart Analysis",
      description: "Your website will feature interactive natal chart generation and analysis tools for your clients."
    },
    {
      icon: <FaSun className="text-5xl mb-6 text-cyber-pink" />,
      title: "Transit Forecasts",
      description: "Automated planetary transit calculations with customized interpretations for your clients."
    },
    {
      icon: <FaMoon className="text-5xl mb-6 text-cyber-pink" />,
      title: "Lunar Phase Tracking",
      description: "Help clients align activities with lunar cycles through an interactive moon phase calendar."
    },
    {
      icon: <GiPalm className="text-5xl mb-6 text-cyber-pink" />,
      title: "Compatibility Analysis",
      description: "Interactive synastry charts and relationship compatibility assessments for your clients."
    },
    {
      icon: <GiStarsStack className="text-5xl mb-6 text-cyber-pink" />,
      title: "Horoscope Generator",
      description: "Daily, weekly, or monthly horoscope content that updates automatically for all zodiac signs."
    },
    {
      icon: <FaGem className="text-5xl mb-6 text-cyber-pink" />,
      title: "Gemstone Recommendations",
      description: "Personalized gemstone and crystal suggestions based on astrological configurations."
    }
  ];

  return (
    <div className="bg-cyber-dark text-cyber-blue overflow-hidden min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-cyber-dark to-cyber-dark/50 z-10"></div>
          <img 
            src="https://images.unsplash.com/photo-1517976487492-5750f3195933?auto=format&fit=crop&w=1920&q=80" 
            alt="Cosmic background" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="container mx-auto px-4 py-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              <span className="text-cyber-pink">Stellar</span> Websites for <span className="text-cyber-yellow">Astrologers</span>
            </h1>
            <p className="text-xl md:text-2xl mb-10 text-gray-300">
              Transform your astrology practice with a professional, cosmic-themed website that attracts clients and grows your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#packages" className="px-8 py-4 bg-cyber-pink text-cyber-dark font-bold rounded-md hover:bg-cyber-yellow hover:text-cyber-dark transition duration-300 text-lg">
                View Packages
              </a>
              <a href="#contact" className="px-8 py-4 bg-transparent border-2 border-cyber-blue text-cyber-blue font-bold rounded-md hover:bg-cyber-blue hover:text-cyber-dark transition duration-300 text-lg">
                Contact Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cosmic Features Banner */}
      <section className="bg-gradient-to-r from-cyber-darker to-cyber-dark py-12 border-t border-b border-cyber-blue/30">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8">
            <div className="flex items-center">
              <FaMoon className="text-cyber-yellow text-2xl mr-2" />
              <span className="font-semibold">Zodiac-Optimized Design</span>
            </div>
            <div className="flex items-center">
              <GiCrystalBall className="text-cyber-yellow text-2xl mr-2" />
              <span className="font-semibold">Client Portal</span>
            </div>
            <div className="flex items-center">
              <FaCalendarAlt className="text-cyber-yellow text-2xl mr-2" />
              <span className="font-semibold">Booking System</span>
            </div>
            <div className="flex items-center">
              <FaStar className="text-cyber-yellow text-2xl mr-2" />
              <span className="font-semibold">Natal Chart Tool</span>
            </div>
          </div>
        </div>
      </section>

      {/* Astrology-specific Services */}
      <section className="py-20 bg-cyber-dark">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-cyber-yellow">Astrology-Focused Features</h2>
            <p className="text-xl max-w-3xl mx-auto">
              Our websites include specialized tools designed specifically for astrologers and spiritual practitioners.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {astrologyServices.map((service, index) => (
              <motion.div 
                key={index}
                whileHover={{ y: -10 }}
                transition={{ type: "spring", stiffness: 300 }}
                className="bg-cyber-darker rounded-lg overflow-hidden border border-cyber-blue p-8 text-center"
              >
                <div className="flex justify-center">{service.icon}</div>
                <h3 className="text-2xl font-bold mb-4 text-white">{service.title}</h3>
                <p className="text-gray-300">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Projects Section */}
      <section className="py-20 bg-cyber-darker">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-cyber-yellow">Our Astrology Website Projects</h2>
            <p className="text-xl max-w-3xl mx-auto">
              Take a look at some of our successful astrology website projects that have helped practitioners expand their reach.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {projectExamples.map((project, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-cyber-dark rounded-lg overflow-hidden border border-cyber-blue hover:border-cyber-pink transition-all duration-300"
              >
                <img 
                  src={project.image} 
                  alt={project.name}
                  className="w-full h-56 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-2xl font-bold mb-2 text-cyber-pink">{project.name}</h3>
                  <p className="mb-4 text-gray-300">{project.description}</p>
                  <ul className="mb-6 space-y-2">
                    {project.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start">
                        <FaCheckCircle className="text-cyber-green mt-1 mr-2 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a 
                    href={project.url}
                    className="block w-full py-2 bg-cyber-blue text-cyber-dark font-bold rounded text-center hover:bg-cyber-pink transition duration-300"
                  >
                    View Project
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <a href="#contact" className="inline-block px-8 py-4 bg-gradient-to-r from-cyber-pink to-cyber-blue text-white font-bold rounded-md hover:from-cyber-yellow hover:to-cyber-yellow transition duration-300 text-lg">
              Start Your Project Today
            </a>
          </div>
        </div>
      </section>
      
      {/* Why Astrologers Need a Professional Website */}
      <section className="py-20 bg-cyber-dark border-t border-cyber-blue/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-cyber-yellow">Why Astrologers Need a Professional Website</h2>
            <p className="text-xl max-w-3xl mx-auto">
              In today's digital age, having a professional online presence is crucial for astrology businesses.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1507499739999-097f9693b6c4?auto=format&fit=crop&w=800&h=600&q=80" 
                alt="Astrologer with client"
                className="rounded-lg shadow-2xl"
              />
            </div>
            <div>
              <ul className="space-y-6">
                <li className="flex gap-4">
                  <div className="bg-cyber-pink text-cyber-dark rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-white">Establish Your Cosmic Authority</h3>
                    <p className="text-gray-300">A professional website positions you as an expert in your astrological specialty, building trust with potential clients.</p>
                  </div>
                </li>
                
                <li className="flex gap-4">
                  <div className="bg-cyber-pink text-cyber-dark rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-white">Reach More Seekers</h3>
                    <p className="text-gray-300">Expand your client base beyond local limits by making your services accessible to anyone searching for astrological guidance online.</p>
                  </div>
                </li>
                
                <li className="flex gap-4">
                  <div className="bg-cyber-pink text-cyber-dark rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-white">Automated Bookings</h3>
                    <p className="text-gray-300">Let clients book and pay for consultations 24/7, even while you're sleeping or providing readings to others.</p>
                  </div>
                </li>
                
                <li className="flex gap-4">
                  <div className="bg-cyber-pink text-cyber-dark rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-white">Showcase Your Unique Gifts</h3>
                    <p className="text-gray-300">Highlight your specific astrological approach, methodologies, and success stories to attract your ideal clients.</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="bg-cyber-darker py-8 border-t border-b border-cyber-blue/30">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="flex items-center">
              <FaCheckCircle className="text-cyber-green text-2xl mr-2" />
              <span className="font-semibold">100% Satisfaction</span>
            </div>
            <div className="flex items-center">
              <BsShield className="text-cyber-green text-2xl mr-2" />
              <span className="font-semibold">Secure Payments</span>
            </div>
            <div className="flex items-center">
              <FaUserCheck className="text-cyber-green text-2xl mr-2" />
              <span className="font-semibold">200+ Happy Clients</span>
            </div>
            <div className="flex items-center">
              <BsLightningCharge className="text-cyber-green text-2xl mr-2" />
              <span className="font-semibold">Fast Delivery</span>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-20 bg-cyber-darker border-t border-cyber-blue/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-cyber-yellow">Our Tech Stack</h2>
            <p className="text-xl max-w-3xl mx-auto">
              We use cutting-edge technologies to build fast, secure, and scalable websites for astrologers.
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-12">
            {technologies.map((tech, index) => (
              <motion.div 
                key={index}
                whileHover={{ y: -10 }}
                className="text-center"
              >
                {tech.icon}
                <p className="mt-2 font-semibold">{tech.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-cyber-dark">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-cyber-yellow">Our Process</h2>
            <p className="text-xl max-w-3xl mx-auto">
              We've streamlined our development process to deliver stellar results efficiently.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {workProcess.map((step, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-cyber-darker rounded-lg p-6 border border-cyber-blue/30 relative"
              >
                <div className="absolute -top-5 -left-5 w-12 h-12 rounded-full bg-cyber-pink text-cyber-dark font-bold text-xl flex items-center justify-center">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold mb-3 text-white mt-4">{step.title}</h3>
                <p className="text-gray-300">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="packages" className="py-20 bg-gradient-to-b from-cyber-darker to-cyber-dark">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-cyber-yellow">Affordable Packages</h2>
            <p className="text-xl max-w-3xl mx-auto">
              Choose the perfect package for your astrology business needs.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {packages.map((pkg, index) => (
              <motion.div 
                key={index}
                whileHover={{ y: -10 }}
                className={`rounded-lg overflow-hidden ${pkg.recommended ? 'border-4 border-cyber-pink' : 'border border-cyber-blue'} bg-cyber-darker relative`}
              >
                {pkg.recommended && (
                  <div className="bg-cyber-pink text-cyber-dark font-bold py-1 text-center">
                    Most Popular
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-2xl font-bold mb-2 text-white">{pkg.name}</h3>
                  <div className="text-3xl font-bold text-cyber-green mb-2">{pkg.price}</div>
                  <p className="text-gray-300 mb-6">{pkg.description}</p>
                  <ul className="mb-8 space-y-2">
                    {pkg.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start">
                        <FaCheckCircle className="text-cyber-green mt-1 mr-2 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div>
                    <label className="flex items-center">
                      <input 
                        type="radio" 
                        name="websiteType" 
                        value={pkg.value} 
                        checked={formData.websiteType === pkg.value} 
                        onChange={handleChange} 
                        className="mr-2"
                      />
                      <span>Select {pkg.name} Package</span>
                    </label>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <a href="#contact" className="inline-block px-8 py-4 bg-cyber-pink text-cyber-dark font-bold rounded-md hover:bg-cyber-yellow transition duration-300 text-lg">
              Get Started
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-cyber-dark">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-cyber-yellow">What Our Clients Say</h2>
            <p className="text-xl max-w-3xl mx-auto">
              Don't just take our word for it — hear from astrologers who've transformed their businesses with our websites.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-cyber-darker rounded-lg p-6 border border-cyber-blue"
              >
                <div className="flex items-center mb-4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-bold text-white">{testimonial.name}</h4>
                    <p className="text-sm text-cyber-pink">{testimonial.business}</p>
                  </div>
                </div>
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} className="text-cyber-yellow mr-1" />
                  ))}
                </div>
                <p className="italic text-gray-300">"{testimonial.quote}"</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-b from-cyber-dark to-cyber-darker">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-cyber-yellow">Ready to Launch Your Cosmic Website?</h2>
            <p className="text-xl max-w-3xl mx-auto">
              Fill out the form below and we'll get back to you within 24 hours to discuss your astrology website needs.
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto bg-cyber-darker p-8 rounded-lg border border-cyber-blue">
            {formStatus.submitted ? (
              <div className="text-center py-12">
                <FaCheckCircle className="text-6xl mx-auto mb-6 text-cyber-green" />
                <h3 className="text-2xl font-bold mb-4 text-white">Message Received!</h3>
                <p className="text-xl">{formStatus.message}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block mb-2 text-cyber-blue">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full p-3 bg-cyber-dark border border-cyber-blue rounded focus:border-cyber-pink outline-none text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-cyber-blue">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full p-3 bg-cyber-dark border border-cyber-blue rounded focus:border-cyber-pink outline-none text-white"
                      required
                    />
                  </div>
                </div>
                
                <div className="mb-6">
                  <label className="block mb-2 text-cyber-blue">Phone Number (optional)</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full p-3 bg-cyber-dark border border-cyber-blue rounded focus:border-cyber-pink outline-none text-white"
                  />
                </div>
                
                <div className="mb-6">
                  <label className="block mb-2 text-cyber-blue">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    className="w-full p-3 bg-cyber-dark border border-cyber-blue rounded focus:border-cyber-pink outline-none text-white"
                    required
                    placeholder="Tell us about your astrology practice and website needs..."
                  ></textarea>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FaLock className="text-cyber-green mr-2" />
                    <span className="text-sm">Your information is secure</span>
                  </div>
                  <button 
                    type="submit"
                    disabled={formStatus.submitting}
                    className="px-8 py-3 bg-cyber-pink text-cyber-dark font-bold rounded-md hover:bg-cyber-yellow transition duration-300 text-lg"
                  >
                    {formStatus.submitting ? 'Sending...' : 'Get Free Consultation'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Trust Elements */}
      <section className="py-12 bg-cyber-darker border-t border-cyber-blue/30">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-8">
            <div className="flex flex-col items-center">
              <div className="bg-cyber-dark p-4 rounded-full mb-3">
                <FaCreditCard className="text-3xl text-cyber-yellow" />
              </div>
              <h4 className="font-semibold">Secure Payments</h4>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-cyber-dark p-4 rounded-full mb-3">
                <FaHandshake className="text-3xl text-cyber-yellow" />
              </div>
              <h4 className="font-semibold">100% Satisfaction</h4>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-cyber-dark p-4 rounded-full mb-3">
                <BsShield className="text-3xl text-cyber-yellow" />
              </div>
              <h4 className="font-semibold">Solid Support</h4>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-cyber-dark p-4 rounded-full mb-3">
                <BsLightningCharge className="text-3xl text-cyber-yellow" />
              </div>
              <h4 className="font-semibold">Fast Delivery</h4>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-12 bg-cyber-dark">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-gradient-to-r from-cyber-blue/20 to-cyber-pink/20 rounded-lg p-8 flex flex-col md:flex-row items-center justify-between border border-cyber-blue">
            <div className="mb-6 md:mb-0 md:mr-6">
              <h3 className="text-2xl font-bold text-white mb-2">Launch Your Astrology Website Today</h3>
              <p className="text-gray-300">Book a free consultation and get a personalized quote for your cosmic digital presence.</p>
            </div>
            <a 
              href="#contact" 
              className="px-6 py-3 bg-cyber-pink text-cyber-dark font-bold rounded-md hover:bg-cyber-yellow transition duration-300 whitespace-nowrap"
            >
              Get Started Now
            </a>
          </div>
        </div>
      </section>

      {/* Choose the Perfect Cosmic Package Banner */}
      <section className="py-12 bg-cyber-dark">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-gradient-to-r from-cyber-blue/20 to-cyber-pink/20 rounded-lg p-8 text-center border border-cyber-blue">
            <h2 className="text-3xl font-bold text-cyber-yellow mb-4">The Stars Are Aligning</h2>
            <p className="text-xl mb-6">Your perfect astrology website is written in the stars. Let us help you manifest it.</p>
            <div className="flex flex-wrap justify-center gap-6">
              <a 
                href="#packages" 
                className="px-6 py-3 bg-cyber-pink text-cyber-dark font-bold rounded-md hover:bg-cyber-yellow transition duration-300"
              >
                View Our Cosmic Packages
              </a>
              <a 
                href="#contact" 
                className="px-6 py-3 bg-transparent border border-cyber-blue text-cyber-blue font-bold rounded-md hover:bg-cyber-blue hover:text-cyber-dark transition duration-300"
              >
                Contact Our Stargazers
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AstrologerSiteLanding; 